
import { DataEngineeringCourse } from './types';

export const DATAENGINEERING_INTERMEDIATE_COURSE: DataEngineeringCourse = {
  id: 'dataengineering-intermediate',
  title: 'Intermediate Data Engineering',
  description: 'Build production-ready data systems with advanced ETL, orchestration, and streaming technologies.',
  level: 'intermediate',
  totalDuration: '16-20 hours',
  image: '/api/placeholder/400/300',
  prerequisites: [
    'Completed Data Engineering Fundamentals',
    'Solid Python programming skills',
    'SQL proficiency',
    'Basic cloud platform knowledge'
  ],
  learningOutcomes: [
    'Design and implement complex ETL/ELT pipelines',
    'Master workflow orchestration with Apache Airflow',
    'Build real-time streaming data processing systems',
    'Design efficient data warehouse schemas',
    'Implement data security and compliance measures',
    'Create robust monitoring and alerting systems',
    'Develop RESTful APIs for data access',
    'Handle big data processing with Spark'
  ],
  modules: [
    {
      id: 'advanced-etl-elt',
      title: 'Advanced ETL/ELT Pipelines',
      description: 'Learn complex data transformation patterns and incremental loading strategies.',
      content: `# Advanced ETL/ELT Pipelines

## ETL vs ELT Paradigms

### ETL (Extract, Transform, Load)
- Transform data before loading
- Good for structured data
- Traditional approach

### ELT (Extract, Load, Transform)
- Load raw data first, transform in destination
- Leverages modern compute power
- Better for big data scenarios

## Incremental Loading Strategies

1. **Timestamp-based**
   - Track last modified timestamp
   - Efficient for append-only data

2. **Change Data Capture (CDC)**
   - Capture database changes in real-time
   - Tools: Debezium, AWS DMS

3. **Delta Loading**
   - Compare source and target
   - Identify changes programmatically

## Complex Transformations

### Data Validation and Quality Checks
- Schema validation
- Data profiling
- Anomaly detection
- Business rule validation

### Handling Data Types and Formats
- Date/time standardization
- Currency conversions
- Text normalization
- Encoding handling`,
      duration: '2.5 hours',
      difficulty: 'intermediate',
      objectives: [
        'Implement incremental loading strategies',
        'Design complex data transformations',
        'Apply data quality validation',
        'Choose between ETL and ELT approaches'
      ],
      codeExamples: [
        {
          title: 'Incremental ETL with Change Detection',
          language: 'python',
          code: `import pandas as pd
from datetime import datetime, timedelta
from typing import Optional
import hashlib

class IncrementalETL:
    def __init__(self, source_conn, target_conn):
        self.source_conn = source_conn
        self.target_conn = target_conn
    
    def get_last_update_timestamp(self, table_name: str) -> Optional[datetime]:
        """Get the last successful update timestamp"""
        query = f"""
        SELECT MAX(last_updated) as last_ts 
        FROM etl_metadata 
        WHERE table_name = '{table_name}'
        """
        result = pd.read_sql(query, self.target_conn)
        return result['last_ts'].iloc[0] if not result.empty else None
    
    def extract_incremental_data(self, table_name: str, 
                                timestamp_column: str) -> pd.DataFrame:
        """Extract only new/modified records"""
        last_update = self.get_last_update_timestamp(table_name)
        
        if last_update:
            where_clause = f"WHERE {timestamp_column} > '{last_update}'"
        else:
            where_clause = ""  # Full load on first run
        
        query = f"SELECT * FROM {table_name} {where_clause}"
        return pd.read_sql(query, self.source_conn)
    
    def detect_changes(self, df: pd.DataFrame, 
                      primary_key: str) -> pd.DataFrame:
        """Detect actual changes using row hashing"""
        # Create row hash for change detection
        df['row_hash'] = df.apply(
            lambda row: hashlib.md5(
                str(row.drop(primary_key)).encode()
            ).hexdigest(), 
            axis=1
        )
        
        # Compare with existing hashes
        existing_hashes = self.get_existing_hashes(df[primary_key].tolist())
        
        # Filter to only changed records
        changed_records = df[
            ~df['row_hash'].isin(existing_hashes['row_hash'])
        ]
        
        return changed_records.drop('row_hash', axis=1)
    
    def update_metadata(self, table_name: str):
        """Update ETL metadata after successful run"""
        metadata = {
            'table_name': table_name,
            'last_updated': datetime.now(),
            'status': 'success'
        }
        pd.DataFrame([metadata]).to_sql(
            'etl_metadata', self.target_conn, 
            if_exists='append', index=False
        )`,
          explanation: 'Production-ready incremental ETL with change detection and metadata tracking.'
        }
      ]
    },
    {
      id: 'workflow-orchestration',
      title: 'Workflow Orchestration with Apache Airflow',
      description: 'Master Apache Airflow for complex workflow scheduling and dependency management.',
      content: `# Workflow Orchestration with Apache Airflow

## Apache Airflow Fundamentals

Apache Airflow is a platform to programmatically author, schedule, and monitor workflows.

### Key Concepts

1. **DAG (Directed Acyclic Graph)**
   - Represents a workflow
   - Defines task dependencies
   - No cycles allowed

2. **Tasks**
   - Individual units of work
   - Various operator types
   - Can pass data between tasks

3. **Operators**
   - PythonOperator: Execute Python functions
   - BashOperator: Run bash commands
   - SQLOperator: Execute SQL queries

## Advanced Airflow Patterns

### Task Dependencies
- Upstream/downstream relationships
- Branch operations
- Dynamic task generation

### Data Passing
- XComs for small data
- External storage for large datasets
- TaskFlow API for cleaner code

### Error Handling
- Retry mechanisms
- Failure callbacks
- Dead letter queues`,
      duration: '3 hours',
      difficulty: 'intermediate',
      objectives: [
        'Create complex DAGs with multiple dependencies',
        'Implement error handling and retry logic',
        'Use XComs for data passing between tasks',
        'Schedule and monitor production workflows'
      ],
      codeExamples: [
        {
          title: 'Complex Airflow DAG with Error Handling',
          language: 'python',
          code: `from airflow import DAG
from airflow.operators.python import PythonOperator
from airflow.operators.email import EmailOperator
from airflow.utils.dates import days_ago
from airflow.utils.trigger_rule import TriggerRule
from datetime import timedelta
import pandas as pd

default_args = {
    'owner': 'data-team',
    'depends_on_past': False,
    'start_date': days_ago(1),
    'email_on_failure': True,
    'email_on_retry': False,
    'retries': 3,
    'retry_delay': timedelta(minutes=5),
    'max_active_runs': 1
}

def extract_data(**context):
    """Extract data with error handling"""
    try:
        # Simulate data extraction
        data = pd.DataFrame({
            'id': range(1000),
            'value': range(1000, 2000)
        })
        
        # Push to XCom for next task
        context['task_instance'].xcom_push(
            key='extracted_count', 
            value=len(data)
        )
        
        return f"Extracted {len(data)} records"
    except Exception as e:
        raise Exception(f"Extraction failed: {str(e)}")

def transform_data(**context):
    """Transform data with validation"""
    # Pull from XCom
    extracted_count = context['task_instance'].xcom_pull(
        task_ids='extract_task',
        key='extracted_count'
    )
    
    if extracted_count < 100:
        raise ValueError("Insufficient data for processing")
    
    # Simulate transformation
    transformed_count = extracted_count * 0.95  # Some filtering
    
    context['task_instance'].xcom_push(
        key='transformed_count',
        value=transformed_count
    )
    
    return f"Transformed {transformed_count} records"

def load_data(**context):
    """Load data to destination"""
    transformed_count = context['task_instance'].xcom_pull(
        task_ids='transform_task',
        key='transformed_count'
    )
    
    # Simulate loading
    print(f"Loading {transformed_count} records to warehouse")
    return "Load completed successfully"

def failure_callback(context):
    """Handle task failures"""
    print(f"Task {context['task_instance'].task_id} failed")
    # Could send alerts, clean up resources, etc.

with DAG(
    'data_pipeline_dag',
    default_args=default_args,
    description='Production data pipeline with error handling',
    schedule_interval='@daily',
    catchup=False,
    on_failure_callback=failure_callback
) as dag:
    
    extract_task = PythonOperator(
        task_id='extract_task',
        python_callable=extract_data
    )
    
    transform_task = PythonOperator(
        task_id='transform_task',
        python_callable=transform_data
    )
    
    load_task = PythonOperator(
        task_id='load_task',
        python_callable=load_data
    )
    
    # Success notification
    success_email = EmailOperator(
        task_id='success_notification',
        to=['data-team@company.com'],
        subject='Data Pipeline Success',
        html_content='<p>Daily data pipeline completed successfully</p>',
        trigger_rule=TriggerRule.ALL_SUCCESS
    )
    
    # Failure notification
    failure_email = EmailOperator(
        task_id='failure_notification',
        to=['data-team@company.com'],
        subject='Data Pipeline Failure',
        html_content='<p>Daily data pipeline failed. Please investigate.</p>',
        trigger_rule=TriggerRule.ONE_FAILED
    )
    
    # Set dependencies
    extract_task >> transform_task >> load_task
    load_task >> success_email
    [extract_task, transform_task, load_task] >> failure_email`,
          explanation: 'Production-ready Airflow DAG with comprehensive error handling, notifications, and data validation.'
        }
      ]
    }
    // ... additional modules would continue here
  ]
};
